import { calculateMinContribution } from "@taabl/common-lib/calculators/contribution";
import { formatCurrency } from "@taabl/common-lib/calculators/money";
import { Common } from "@taabl/common-lib/constants/common";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as yup from "yup";
import FormikCheckbox from "../../components/Forms/FormikCheckbox";
import FormikTextField from "../../components/Forms/FormikTextField";
import LoaderButton from "../../components/LoaderButton";
import PageLoader from "../../components/PageLoader";
import photo1 from "../../images/writingpad.svg";
import FormImage from "../../layouts/formimage";
import { Constants } from "../../libs/Constants";
import { getList } from "../../libs/dbFunctions";
import { calculateMaxContribution } from "../../libs/utils";
import LoanAgreement from "./LoanAgreement";
import MoneyCalculator from "./MoneyCalculator";

const STEPS = {
  SALARY_DETAIL: "salary_detail",
  LOAN_AGREEMENT: "loan_agreement",
  SUCCESS: "success",
};
const MONTH_PER_YEAR = 12;
const Personalization = (props) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(STEPS.SALARY_DETAIL);
  const [formFields, setFormFields] = useState({});
  const [allowEdit, setAllowEdit] = useState(false);
  const [plan, setPlan] = useState(null);
  const [maxContributionAmount, setMaxContributionAmount] = useState(null);
  const [minContributionAmount, setMinContributionAmount] = useState(null);
  const [contributionAmount, setContributionAmount] = useState(null);
  const [periodsPerAnnum, setPeriodsPerAnnum] = useState(null);
  const [salary, setSalary] = useState(0);
  const [initialValues, setInitialValues] = useState({
    salary: props?.userObj?.salary || 0,
    contributionAmount: 0,
    currentlyEnrolled: "",
    notEnrolled: "",
    address: "",
    city: "",
    zip: "",
    state: "",
    country: "USA",
    phoneNumber: "",
  });

  const schema = yup.object({
    salary: yup
      .number()
      .required("Salary is required")
      .positive("Salary should be positive"),
    contributionAmount: yup
      .number()
      .test("contributionAmount", function(value) {
        if (!value || value === 0 || value < 0) {
          return this.createError({
            message: "Please come back after you've signed up for your ESPP.",
            path: "contributionAmount",
          });
        }
        if (minContributionAmount && value < minContributionAmount) {
          return this.createError({
            message: `Contribution amount can’t be lower than ${formatCurrency(
              minContributionAmount
            )}`,
            path: "contributionAmount",
          });
        }
        if (maxContributionAmount && value > maxContributionAmount) {
          return this.createError({
            message: `Contribution amount can’t exceed ${formatCurrency(
              maxContributionAmount
            )}`,
            path: "contributionAmount",
          });
        }
        return true;
      }),
    currentlyEnrolled: yup.boolean(),
    notEnrolled: yup.boolean(),
  });

  useEffect(() => {
    function load() {
      getPlanDetail();
    }
    load();
  }, [props.activeCompany]);

  useEffect(() => {
    function load() {
      if (props.userObj.salary) {
        setSalary(initialValues.salary);
      }
    }
    load();
  }, [props.userObj.salary]);

  useEffect(() => {
    function load() {
      if (plan) {
        let amount = calculateMaxAllowedContribution(initialValues.salary);
        calculateMinAllowedContribution(initialValues.salary);
        setContributionAmount(amount);
        setInitialValues({
          ...initialValues,
          contributionAmount: salary !== 0 ? amount : 0,
        });
      }
    }
    load();
  }, [plan]);

  const getPlanDetail = async () => {
    setLoading(true);
    try {
      let planDetail = await getList(
        Constants.dbTypes.PLAN_DETAILS,
        props.activeCompany
      );
      if (planDetail && planDetail.length > 0) {
        setPlan(planDetail[0]);
      }
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };
  const calculatePeriodsPerAnnum = () => {
    let purchasePeriods = MONTH_PER_YEAR / Number(plan.purchasePeriod);
    setPeriodsPerAnnum(purchasePeriods);
    return purchasePeriods;
  };
  const calculateMinAllowedContribution = (salary = 0) => {
    //this method calculates the maximum contribution allowed per purchase period
    let amount = calculateMinContribution(
      plan?.minContribution,
      salary,
      plan?.purchasePeriod
    );
    // min contribution amount per annum
    amount = amount * calculatePeriodsPerAnnum();
    setMinContributionAmount(amount);
    return amount;
  };
  const calculateMaxAllowedContribution = (salary = 0) => {
    //this method calculates the maximum contribution allowed per purchase period
    if (!salary) {
      return 0;
    }
    let amount = calculateMaxContribution(
      plan?.maxContribution,
      salary,
      plan?.purchasePeriod
    );
    // max contribution amount per annum
    amount = amount * calculatePeriodsPerAnnum();
    setMaxContributionAmount(amount);
    return amount;
  };
  const handleSubmit = async (values) => {
    setFormFields(values);
    setStep(STEPS.LOAN_AGREEMENT);
    window.scrollTo(0, 0);
  };
  const renderOnboardingSucess = () => {
    window.location.href = "/onboardingcomplete";
  };

  if (!Common.ALLOWED_STATES_FOR_SIGNUP.includes(props.userObj.state)) {
    return (
      <PageLoader isActive={loading}>
        <FormImage
          title="See the Benefit"
          subtitle="Taabl will offset your payroll deduction with a risk free loan directly into your bank account. When the stock is issued, Taabl will help sell a portion to repay the loan and the 6% fee."
          image={photo1}
          bgClass="background-purple image-bg"
          imageOverlay={
            <MoneyCalculator
              plan={plan}
              contributionAmount={contributionAmount}
              salary={salary}
            ></MoneyCalculator>
          }
        >
          <React.Fragment>
            <h2 className="text-center pb-3">Personalization</h2>
          </React.Fragment>
          <div className="font-secondary text-center font-size-10 pb-2">
            We are working to be able to legally lend in your area. We will send
            you an e-mail when we are able to lend in your area.
          </div>
        </FormImage>
      </PageLoader>
    );
  }

  return (
    <PageLoader isActive={loading}>
      {step && step === STEPS.SALARY_DETAIL && (
        <FormImage
          title="See the Benefit"
          subtitle="Taabl will offset your payroll deduction with a risk free loan directly into your bank account. When the stock is issued, Taabl will help sell a portion to repay the loan and the 6% fee."
          image={photo1}
          bgClass="background-purple image-bg"
          imageOverlay={
            <MoneyCalculator
              plan={plan}
              contributionAmount={contributionAmount}
              salary={salary}
            ></MoneyCalculator>
          }
        >
          <React.Fragment>
            <h2 className="text-center pb-3">Personalization</h2>
          </React.Fragment>

          <div className="font-secondary text-center font-size-10 pb-2">
            Taabl will deposit into your account each month.
          </div>
          <Formik
            validationSchema={schema}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            enableReinitialize
            validateOnMount
          >
            {({
              handleSubmit,
              isValid,
              isSubmitting,
              values,
              setFieldValue,
            }) => (
              <form noValidate onSubmit={handleSubmit} className="w-100">
                <FormikTextField
                  size="lg"
                  controlId="salary"
                  label="Annual Salary"
                  type="number"
                  name="salary"
                  onChange={(name, value) => {
                    setSalary(Number(value));
                    let maxAllowed = calculateMaxAllowedContribution(value);
                    calculateMinAllowedContribution(value);
                    setFieldValue("contributionAmount", maxAllowed);
                    setContributionAmount(maxAllowed);
                  }}
                  className="mb-4"
                />
                <FormikTextField
                  size="lg"
                  controlId="contributionAmount"
                  label="Annual Contribution Amount"
                  type="number"
                  disabled={!allowEdit}
                  onChange={null}
                  name="contributionAmount"
                  onChange={(name, value) => {
                    setContributionAmount(value);
                  }}
                  className="mb-2"
                />
                <div className="mb-4">
                  <span className="form-label font-primary">
                    Contributing less?
                  </span>
                  <a
                    size="sm"
                    className="text-secondary fw-bold ms-2 font-size-4 cursor-pointer"
                    onClick={() => {
                      setAllowEdit(true);
                    }}
                  >
                    Edit
                  </a>
                </div>
                <span className="form-label font-weight-normal">
                  Select One:
                </span>
                <FormikCheckbox
                  controlId="currentlyEnrolled"
                  name="currentlyEnrolled"
                  md="6"
                  sm="12"
                  className="my-2"
                  onChange={(e, form) => {
                    if (e.target.checked) {
                      form.setFieldValue("notEnrolled", false);
                    }
                  }}
                  label={
                    <span className="form-label font-weight-normal">
                      I am currently enrolled in my ESPP for the amount above.
                    </span>
                  }
                />

                <FormikCheckbox
                  controlId="notEnrolled"
                  name="notEnrolled"
                  className="my-4"
                  md="6"
                  sm="12"
                  onChange={(e, form) => {
                    if (e.target.checked) {
                      form.setFieldValue("currentlyEnrolled", false);
                    }
                  }}
                  label={
                    <span className="form-label font-weight-normal">
                      I am not yet enrolled, but I will be enrolling in my ESPP
                      for the amount above.
                    </span>
                  }
                />
                <LoaderButton
                  block
                  type="submit"
                  variant="primary"
                  size="lg"
                  text="Next"
                  className="mb-2"
                  isLoading={isSubmitting}
                  disabled={
                    (!isValid && values.contributionAmount === 0) ||
                    isSubmitting ||
                    (!values.currentlyEnrolled && !values.notEnrolled)
                  }
                >
                  Next
                </LoaderButton>
              </form>
            )}
          </Formik>
        </FormImage>
      )}

      {step && step === STEPS.LOAN_AGREEMENT && (
        <LoanAgreement
          formFields={formFields}
          periodsPerAnnum={periodsPerAnnum}
          user={props.userObj}
          company={props.activeCompany}
          onSuccess={() => {
            setStep(STEPS.SUCCESS);
          }}
          onCancel={() => {
            setStep(STEPS.SALARY_DETAIL);
          }}
        ></LoanAgreement>
      )}
      {step && step === STEPS.SUCCESS && renderOnboardingSucess()}
    </PageLoader>
  );
};

export default Personalization;
